import React from 'react';
import { cn } from '../../utils/cn';

/**
 * StatusIndicator — pattern signature piste 8 : « ● Label » en uppercase font-semibold.
 *
 * À utiliser à la place de Badge pour les statuts métier (publié, brouillon,
 * archivé, envoyé, en attente…). Plus typographique, moins « pilule colorée ».
 *
 *   <StatusIndicator tone="ink">Publié</StatusIndicator>
 *   <StatusIndicator tone="muted">Archivé</StatusIndicator>
 *   <StatusIndicator tone="warn">Brouillon</StatusIndicator>
 *
 * Sizes :
 *   - sm : text-[11.5px]
 *   - md : text-[12.5px] (par défaut)
 *   - lg : text-[13.5px]
 *
 * Le `dot` peut être désactivé (`dot={false}`) si l'icône suffit.
 */

const TONES = {
  ink:     { dot: 'bg-ink',      text: 'text-text' },
  muted:   { dot: 'bg-mute-2',   text: 'text-mute' },
  faded:   { dot: 'bg-ink/40',   text: 'text-ink/60' },
  cream:   { dot: 'bg-cream',    text: 'text-cream' },
  success: { dot: 'bg-success',  text: 'text-success' },
  warn:    { dot: 'bg-warn',     text: 'text-warn' },
  danger:  { dot: 'bg-danger',   text: 'text-danger' },
  info:    { dot: 'bg-info',     text: 'text-info' },
};

const SIZES = {
  sm: { text: 'text-[11.5px]', dot: 'w-1 h-1' },
  md: { text: 'text-[12.5px]', dot: 'w-1.5 h-1.5' },
  lg: { text: 'text-[13.5px]', dot: 'w-2 h-2' },
};

export function StatusIndicator({
  tone = 'ink',
  size = 'md',
  dot = true,
  uppercase = false,
  className,
  children,
  ...rest
}) {
  const t = TONES[tone] || TONES.ink;
  const s = SIZES[size] || SIZES.md;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 font-semibold whitespace-nowrap',
        s.text,
        t.text,
        uppercase && 'uppercase tracking-wider',
        className,
      )}
      {...rest}
    >
      {dot && <span className={cn('rounded-full shrink-0', s.dot, t.dot)} aria-hidden />}
      {children}
    </span>
  );
}

export default StatusIndicator;
